import { apiClient, isApiError } from "@/lib/apiClient";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { AuthContext, type AuthStatus } from "@/components/AuthContext";
import {
	useHasUnsavedChanges,
	useUnsavedChangesDialog,
} from "@/hooks/unsavedChangesDialog";
import type { User } from "@/lib/types";

function isAuthFailure(error: unknown): boolean {
	return (
		isApiError(error) &&
		(error.response?.status === 401 || error.response?.status === 403)
	);
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const [status, setStatus] = useState<AuthStatus>("checking");
	const [user, setUser] = useState<User | null>(null);
	const hasUnsavedChanges = useHasUnsavedChanges();
	const confirmUnsavedChanges = useUnsavedChangesDialog();
	const expirationPromptInFlightRef = useRef(false);

	const confirmDraftLoss = useCallback(async (): Promise<boolean> => {
		if (!hasUnsavedChanges) return true;
		const message = t("common.unsavedChangesMessage");
		return confirmUnsavedChanges
			? confirmUnsavedChanges(message)
			: window.confirm(message);
	}, [confirmUnsavedChanges, hasUnsavedChanges, t]);

	const refresh = useCallback(async (): Promise<User | null> => {
		try {
			const { data } = await apiClient.get<User>("/api/auth/me");
			setUser(data);
			setStatus("authenticated");
			return data;
		} catch (error) {
			if (isAuthFailure(error)) {
				setUser(null);
				setStatus("guest");
				return null;
			}
			setStatus((current) =>
				current === "authenticated" ? "authenticated" : "unavailable",
			);
			return null;
		}
	}, []);

	const logout = useCallback(async (): Promise<boolean> => {
		const confirmed = await confirmDraftLoss();
		if (!confirmed) return false;
		try {
			await apiClient.post("/api/auth/logout");
		} finally {
			setUser(null);
			setStatus("guest");
		}
		return true;
	}, [confirmDraftLoss]);

	const expireSession = useCallback(async (): Promise<void> => {
		if (expirationPromptInFlightRef.current) return;
		expirationPromptInFlightRef.current = true;
		try {
			const confirmed = await confirmDraftLoss();
			if (!confirmed) return;
			setUser(null);
			setStatus("guest");
		} finally {
			expirationPromptInFlightRef.current = false;
		}
	}, [confirmDraftLoss]);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	useEffect(() => {
		const handleExpired = () => {
			void expireSession();
		};
		window.addEventListener("kitsulab-auth-expired", handleExpired);
		return () => {
			window.removeEventListener("kitsulab-auth-expired", handleExpired);
		};
	}, [expireSession]);

	const value = useMemo(
		() => ({ status, user, refresh, logout }),
		[status, user, refresh, logout],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
